import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Loader2, Mail, Phone, RefreshCw, LogOut, Inbox, Users, Download } from "lucide-react";
import { Input } from "../components/ui/input";
import { SunriseCircle } from "../components/icons/SunriseCircle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "bt_admin_code";
const SEEN_KEY = "bt_seen_leads";

const latestTs = (list) => list.reduce((m, x) => (x.created_at && x.created_at > m ? x.created_at : m), "");

const csvCell = (v) => {
  const s = v === undefined || v === null ? "" : String(v);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadCSV = (filename, headers, rows) => {
  const lines = [headers.map(csvCell).join(",")];
  rows.forEach((r) => lines.push(r.map(csvCell).join(",")));
  const csv = "\ufeff" + lines.join("\r\n"); // BOM for Excel
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const interestLabel = {
  discovery: "Free discovery call",
  renew: "The Renew Experience",
  flourish: "The Flourish Experience",
  unsure: "Not sure yet",
};

export default function Admin() {
  const [code, setCode] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("inquiries");
  const [inquiries, setInquiries] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [lastSeen, setLastSeen] = useState(() => {
    try { return JSON.parse(localStorage.getItem(SEEN_KEY) || "{}"); } catch { return {}; }
  });

  const markSeen = useCallback((tabId, list) => {
    const ts = latestTs(list) || new Date().toISOString();
    setLastSeen((prev) => {
      if (prev[tabId] === ts) return prev;
      const next = { ...prev, [tabId]: ts };
      localStorage.setItem(SEEN_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const unreadCount = (tabId, list) => {
    const seen = lastSeen[tabId];
    if (seen === undefined || seen === null) return 0;
    const seenTime = Date.parse(seen);
    return list.filter((x) => x.created_at && Date.parse(x.created_at) > seenTime).length;
  };

  const fetchData = useCallback(async (accessCode) => {
    setLoading(true);
    try {
      const headers = { "X-Admin-Code": accessCode };
      const [b, w] = await Promise.all([
        axios.get(`${API}/bookings`, { headers }),
        axios.get(`${API}/waitlist`, { headers }),
      ]);
      setInquiries(b.data);
      setWaitlist(w.data);
      setAuthed(true);
      localStorage.setItem(STORAGE_KEY, accessCode);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Incorrect access code.");
        localStorage.removeItem(STORAGE_KEY);
        setAuthed(false);
      } else {
        toast.error("Could not load submissions. Try again.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      setCode(saved);
      fetchData(saved);
    }
  }, [fetchData]);

  // Mark the active tab as seen; baseline the other tab on first ever load so old leads don't show as "new".
  useEffect(() => {
    if (!authed) return;
    const activeList = tab === "inquiries" ? inquiries : waitlist;
    markSeen(tab, activeList);
    const other = tab === "inquiries" ? "waitlist" : "inquiries";
    const otherList = tab === "inquiries" ? waitlist : inquiries;
    setLastSeen((prev) => {
      if (prev[other] !== undefined) return prev;
      const next = { ...prev, [other]: latestTs(otherList) };
      localStorage.setItem(SEEN_KEY, JSON.stringify(next));
      return next;
    });
  }, [authed, inquiries, waitlist, tab, markSeen]);

  const submit = (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    fetchData(code.trim());
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setAuthed(false);
    setCode("");
    setInquiries([]);
    setWaitlist([]);
  };

  const exportActive = () => {
    const stamp = new Date().toISOString().slice(0, 10);
    if (tab === "inquiries") {
      if (inquiries.length === 0) { toast.error("No discovery calls to export yet."); return; }
      downloadCSV(
        `discovery-calls_${stamp}.csv`,
        ["Name", "Email", "Phone", "Interest", "Message", "Submitted"],
        inquiries.map((q) => [q.name, q.email, q.phone, q.package_interest, q.message, q.created_at ? new Date(q.created_at).toLocaleString() : ""])
      );
    } else {
      if (waitlist.length === 0) { toast.error("No priority-list signups to export yet."); return; }
      downloadCSV(
        `priority-list_${stamp}.csv`,
        ["Name", "Email", "Phone", "Cohort", "Submitted"],
        waitlist.map((w) => [w.name, w.email, w.phone, w.cohort, w.created_at ? new Date(w.created_at).toLocaleString() : ""])
      );
    }
    toast.success("CSV downloaded.");
  };

  if (!authed) {
    return (
      <div data-testid="admin-page" className="min-h-screen bg-brand-cream flex items-center justify-center px-6 pt-24">
        <form onSubmit={submit} className="w-full max-w-sm text-center" data-testid="admin-login-form">
          <SunriseCircle className="text-brand-gold mx-auto mb-6" size={40} strokeWidth={1.3} />
          <h1 className="font-serif text-4xl text-brand-forest tracking-tight">Dashboard</h1>
          <p className="text-brand-inkSoft mt-3 mb-8">Enter your access code to view submissions.</p>
          <div className="relative">
            <Lock size={16} className="absolute left-0 top-1/2 -translate-y-1/2 text-brand-inkSoft" />
            <Input
              type="password"
              data-testid="admin-code-input"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Access code"
              className="bg-transparent border-0 border-b border-brand-forest/25 rounded-none pl-6 pr-0 focus-visible:ring-0 focus-visible:border-brand-gold text-brand-forest text-lg text-center"
            />
          </div>
          <button
            type="submit"
            data-testid="admin-login-btn"
            disabled={loading}
            className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-brand-forest text-brand-cream px-8 py-4 rounded-full text-sm tracking-wide hover:-translate-y-0.5 transition-transform duration-300 disabled:opacity-60"
          >
            {loading ? <><Loader2 size={16} className="animate-spin" /> Checking…</> : "Enter dashboard"}
          </button>
        </form>
      </div>
    );
  }

  const tabs = [
    { id: "inquiries", label: "Discovery Calls", count: inquiries.length, icon: Inbox },
    { id: "waitlist", label: "Priority List", count: waitlist.length, icon: Users },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-brand-cream pt-32 pb-24 px-6 md:px-10">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <span className="overline">Private Dashboard</span>
            <h1 className="font-serif text-4xl md:text-5xl text-brand-forest tracking-tight mt-3">
              Your Leads
            </h1>
          </div>
          <div className="flex gap-2 flex-wrap justify-end">
            <button
              data-testid="admin-export"
              onClick={exportActive}
              className="inline-flex items-center gap-2 border border-brand-forest/20 text-brand-forest px-4 py-2.5 rounded-full text-sm hover:bg-brand-beige transition-colors"
            >
              <Download size={15} /> Export CSV
            </button>
            <button
              data-testid="admin-refresh"
              onClick={() => fetchData(code)}
              className="inline-flex items-center gap-2 border border-brand-forest/20 text-brand-forest px-4 py-2.5 rounded-full text-sm hover:bg-brand-beige transition-colors"
            >
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="inline-flex items-center gap-2 text-brand-inkSoft px-4 py-2.5 rounded-full text-sm hover:text-brand-forest transition-colors"
            >
              <LogOut size={15} /> Lock
            </button>
          </div>
        </div>

        {/* tabs */}
        <div className="flex gap-2 mb-10 border-b border-brand-beige">
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            const unread = unreadCount(t.id, t.id === "inquiries" ? inquiries : waitlist);
            return (
              <button
                key={t.id}
                data-testid={`admin-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`relative inline-flex items-center gap-2 px-5 py-3 text-sm tracking-wide border-b-2 -mb-px transition-colors ${
                  active
                    ? "border-brand-gold text-brand-forest"
                    : "border-transparent text-brand-inkSoft hover:text-brand-forest"
                }`}
              >
                <Icon size={16} /> {t.label}
                <span className={`text-xs px-2 py-0.5 rounded-full ${active ? "bg-brand-gold text-brand-forest" : "bg-brand-beige text-brand-inkSoft"}`}>{t.count}</span>
                {unread > 0 && !active && (
                  <span
                    data-testid={`admin-tab-unread-${t.id}`}
                    className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-brand-forest text-brand-cream text-[0.65rem] font-medium leading-none animate-pulse"
                    title={`${unread} new since you last looked`}
                  >
                    {unread} new
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Discovery calls / inquiries */}
        {tab === "inquiries" && (
          inquiries.length === 0 ? (
            <div className="text-center py-24 text-brand-inkSoft" data-testid="admin-empty-inquiries">
              <Inbox size={40} className="mx-auto mb-5 text-brand-sage" strokeWidth={1.2} />
              <p className="font-serif text-2xl text-brand-forest">No discovery-call requests yet</p>
              <p className="mt-2">New submissions from the homepage form will appear here.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {inquiries.map((q) => (
                <div key={q.id} data-testid="inquiry-card" className="bg-white rounded-2xl p-6 md:p-8 border border-brand-beige">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="font-serif text-2xl text-brand-forest">{q.name}</h3>
                      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-2 text-sm text-brand-inkSoft">
                        <a href={`mailto:${q.email}`} className="inline-flex items-center gap-1.5 hover:text-brand-forest transition-colors">
                          <Mail size={14} /> {q.email}
                        </a>
                        {q.phone ? (
                          <span className="inline-flex items-center gap-1.5"><Phone size={14} /> {q.phone}</span>
                        ) : null}
                      </div>
                    </div>
                    <div className="text-right">
                      {q.package_interest ? (
                        <span className="inline-block bg-brand-beige text-brand-forest text-xs px-3 py-1 rounded-full">
                          {interestLabel[q.package_interest] || q.package_interest}
                        </span>
                      ) : null}
                      <p className="text-xs text-brand-sage mt-2">
                        {q.created_at ? new Date(q.created_at).toLocaleString() : ""}
                      </p>
                    </div>
                  </div>
                  {q.message ? (
                    <p className="text-brand-ink mt-4 leading-relaxed border-t border-brand-beige pt-4 italic">
                      "{q.message}"
                    </p>
                  ) : null}
                  <a
                    href={`mailto:${q.email}?subject=Re: Your inquiry with Brighter Tomorrows`}
                    className="inline-flex items-center gap-2 mt-4 text-sm text-brand-forest link-underline"
                  >
                    Reply to {q.name.split(" ")[0]} →
                  </a>
                </div>
              ))}
            </div>
          )
        )}

        {/* Priority list / waitlist */}
        {tab === "waitlist" && (
          waitlist.length === 0 ? (
            <div className="text-center py-24 text-brand-inkSoft" data-testid="admin-empty-waitlist">
              <Users size={40} className="mx-auto mb-5 text-brand-sage" strokeWidth={1.2} />
              <p className="font-serif text-2xl text-brand-forest">No priority-list signups yet</p>
              <p className="mt-2">Women who join The Gathering priority list will appear here.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {waitlist.map((w) => (
                <div key={w.id} data-testid="waitlist-card" className="bg-white rounded-2xl p-6 md:p-8 border border-brand-beige">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="font-serif text-2xl text-brand-forest">{w.name}</h3>
                      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-2 text-sm text-brand-inkSoft">
                        <a href={`mailto:${w.email}`} className="inline-flex items-center gap-1.5 hover:text-brand-forest transition-colors">
                          <Mail size={14} /> {w.email}
                        </a>
                        {w.phone ? (
                          <span className="inline-flex items-center gap-1.5"><Phone size={14} /> {w.phone}</span>
                        ) : null}
                      </div>
                    </div>
                    <div className="text-right">
                      {w.cohort ? (
                        <span className="inline-block bg-brand-forest text-brand-cream text-xs px-3 py-1 rounded-full">
                          {w.cohort}
                        </span>
                      ) : null}
                      <p className="text-xs text-brand-sage mt-2">
                        {w.created_at ? new Date(w.created_at).toLocaleString() : ""}
                      </p>
                    </div>
                  </div>
                  <a
                    href={`mailto:${w.email}?subject=The Gathering — you're on the priority list`}
                    className="inline-flex items-center gap-2 mt-4 text-sm text-brand-forest link-underline"
                  >
                    Reach out to {w.name.split(" ")[0]} →
                  </a>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}
